import { useState, useEffect, useRef, useCallback } from "react";
import { logger } from "@/core/services/logger";
import { Button } from "@/shared/components/ui/button";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  MessageCircle,
  X,
  Send,
  Calculator,
  Plus,
  HelpCircle,
  Headphones,
  History,
  Trash2,
  Clock,
  EyeOff,
  Settings,
  MoreHorizontal,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/shared/components/ui/avatar";
import { cn } from "@/shared/utils/cn";
import { toast } from "sonner";
import { useDrPhillipPreference, type HideDuration } from "@/shared/hooks";
import { useNavigate } from "react-router-dom";
import { useStreamingChat, type Message } from "../hooks/useStreamingChat";
import { useChatHistory, type ChatSession } from "../hooks/useChatHistory";
import { ChatMessageList } from "./ChatMessageList";
import { useDrPhillipPanel } from "../context/DrPhillipContext";
import { ScrollArea } from "@/shared/components/ui/scroll-area";

export function DrPhillipChat() {
  const navigate = useNavigate();
  const { isOpen, setIsOpen } = useDrPhillipPanel();
  const { isEnabled, isHidden, hideFor } = useDrPhillipPreference();
  const { sendMessage, cancelRequest } = useStreamingChat();
  const { chatSessions, currentChatId, setCurrentChatId, loadChatSessions, saveChat, deleteChat } =
    useChatHistory();

  const [showHistory, setShowHistory] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [connectionState, setConnectionState] = useState<
    "idle" | "connecting" | "streaming" | "error"
  >("idle");
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleHide = (duration: HideDuration) => {
    hideFor(duration);
    setIsOpen(false);
    const durationText = {
      "1hour": "1 hour",
      "1day": "1 day",
      "1week": "1 week",
      forever: "permanently",
    }[duration];
    toast.info(`Dr. Phillip hidden for ${durationText}`, {
      description: "Reactivate in Settings → General → Chat Assistant",
      action: {
        label: "Settings",
        onClick: () => navigate("/settings/account"),
      },
    });
  };

  useEffect(() => {
    if (isOpen && isEnabled && !isHidden) {
      loadChatSessions();
    }
  }, [isOpen, isEnabled, isHidden, loadChatSessions]);

  useEffect(() => {
    if (messages.length > 0 && !isTyping && isEnabled && !isHidden) {
      saveChat(messages, currentChatId);
    }
  }, [messages, isTyping, isEnabled, isHidden, currentChatId, saveChat]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = { role: "user", content: input.trim() };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);
    setIsTyping(true);
    setConnectionState("connecting");

    await sendMessage(
      [...messages, userMessage],
      (content) => {
        setConnectionState("streaming");
        setMessages((prev) => {
          const newMessages = [...prev];
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage?.role === "assistant" && !lastMessage.isError) {
            newMessages[newMessages.length - 1] = { ...lastMessage, content };
          } else {
            newMessages.push({ role: "assistant", content });
          }
          return newMessages;
        });
      },
      () => {
        setIsTyping(false);
        setIsLoading(false);
        setConnectionState("idle");
      },
      (error) => {
        logger.error("Chat error:", error);
        setConnectionState("error");
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: error.message || "I'm temporarily unavailable. Please try again.",
            isError: true,
          },
        ]);
        setIsTyping(false);
        setIsLoading(false);
      }
    );
  }, [input, isLoading, messages, sendMessage]);

  const handleRetry = useCallback(() => {
    const lastUserMessageIndex = messages.map((m) => m.role).lastIndexOf("user");
    if (lastUserMessageIndex >= 0) {
      const lastUserMessage = messages[lastUserMessageIndex];
      setMessages((prev) => prev.slice(0, lastUserMessageIndex));
      setInput(lastUserMessage.content);
    }
  }, [messages]);

  const handleCancelRequest = useCallback(() => {
    cancelRequest();
    setIsLoading(false);
    setIsTyping(false);
    setConnectionState("idle");
  }, [cancelRequest]);

  if (!isEnabled || isHidden) {
    return null;
  }

  const handleQuickAction = (action: string) => {
    setInput(action);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleLiveOperator = () => {
    toast.info("Connecting to live operator...", {
      description: "A team member will respond via email within 1 business hour.",
    });
  };

  const handleNewChat = () => {
    setMessages([]);
    setCurrentChatId(null);
    setShowHistory(false);
  };

  const handleLoadChat = (session: ChatSession) => {
    setMessages(session.messages);
    setCurrentChatId(session.id);
    setShowHistory(false);
  };

  const handleDeleteChat = async (chatId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await deleteChat(chatId);
      if (currentChatId === chatId) {
        handleNewChat();
      }
      toast.success("Chat deleted");
    } catch (error) {
      logger.error("Failed to delete chat:", error);
      toast.error("Failed to delete chat");
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[2px] transition-opacity md:bg-transparent md:backdrop-blur-none"
        onClick={() => setIsOpen(false)}
        aria-hidden
      />

      <div
        className={cn(
          "fixed top-0 right-0 z-50 flex h-full w-full max-w-[380px] flex-col border-l bg-background shadow-xl transition-transform duration-200 ease-out md:w-[380px]",
          isOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        {/* Minimal header */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-3">
            <Avatar className="h-9 w-9 border border-border">
              <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                DP
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-sm font-semibold">Dr. Phillip</h3>
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <span
                  className={cn(
                    "inline-block h-1.5 w-1.5 rounded-full",
                    connectionState === "connecting"
                      ? "bg-amber-500 animate-pulse"
                      : connectionState === "error"
                        ? "bg-destructive"
                        : "bg-emerald-500"
                  )}
                />
                {connectionState === "connecting"
                  ? "Connecting..."
                  : connectionState === "streaming"
                    ? "Typing..."
                    : "Online"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setShowHistory(!showHistory)}
              title="Chat history"
            >
              <History className="h-4 w-4" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => setIsOpen(false)}>
                  <X className="h-4 w-4 mr-2" />
                  Close
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="flex items-center gap-2 text-muted-foreground font-normal">
                  <EyeOff className="h-4 w-4" />
                  Hide assistant
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={() => handleHide("1hour")}>
                  <Clock className="h-4 w-4 mr-2" />
                  1 hour
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleHide("1day")}>
                  <Clock className="h-4 w-4 mr-2" />
                  1 day
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleHide("1week")}>
                  <Clock className="h-4 w-4 mr-2" />
                  1 week
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleHide("forever")} className="text-destructive">
                  <EyeOff className="h-4 w-4 mr-2" />
                  Permanently
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings/account")}>
                  <Settings className="h-4 w-4 mr-2" />
                  Settings
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setIsOpen(false)}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Chat history sidebar */}
          {showHistory && (
            <div className="w-56 shrink-0 border-r flex flex-col">
              <div className="p-3 border-b">
                <Button onClick={handleNewChat} className="w-full" size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  New Chat
                </Button>
              </div>
              <ScrollArea className="flex-1">
                <div className="p-2 space-y-1">
                  {chatSessions.map((session) => (
                    <div
                      key={session.id}
                      onClick={() => handleLoadChat(session)}
                      className={cn(
                        "p-3 rounded-lg cursor-pointer hover:bg-muted/50 group relative",
                        currentChatId === session.id && "bg-muted"
                      )}
                    >
                      <p className="text-sm font-medium truncate pr-6">{session.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(session.updated_at).toLocaleDateString()}
                      </p>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100"
                        onClick={(e) => handleDeleteChat(session.id, e)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  {chatSessions.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-8">No history yet</p>
                  )}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Main chat area */}
          <div className="flex flex-1 flex-col min-w-0">
            {messages.length === 0 && (
              <div className="p-4 space-y-4 border-b">
                <div className="flex items-start gap-3">
                  <Avatar className="h-8 w-8 border shrink-0">
                    <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
                      DP
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 bg-muted/50 rounded-lg rounded-tl-sm px-3 py-2.5">
                    <p className="text-sm leading-relaxed">
                      I&apos;m <span className="font-medium">Dr. Phillip</span>, your direct mail
                      expert. I can help with campaigns, budgets, and platform questions.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickAction("How do I create a new campaign?")}
                    className="justify-start h-9"
                  >
                    <Plus className="h-4 w-4 mr-2 text-primary" />
                    Create Campaign
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickAction("Help me calculate my campaign budget")}
                    className="justify-start h-9"
                  >
                    <Calculator className="h-4 w-4 mr-2 text-primary" />
                    Budget Calculator
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickAction("What can this platform do?")}
                    className="justify-start h-9"
                  >
                    <HelpCircle className="h-4 w-4 mr-2 text-primary" />
                    Platform Help
                  </Button>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLiveOperator}
                  className="w-full justify-center gap-2 h-9"
                >
                  <Headphones className="h-4 w-4" />
                  Talk to Live Operator
                </Button>
              </div>
            )}

            <ChatMessageList
              messages={messages}
              isTyping={isTyping}
              connectionState={connectionState}
              scrollRef={scrollRef}
              onRetry={handleRetry}
              onLiveOperator={handleLiveOperator}
              isLoading={isLoading}
            />

            <div className="p-3 border-t shrink-0">
              <div className="flex gap-2 items-end">
                <div className="flex-1 min-w-0">
                  <Textarea
                    ref={textareaRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type your message..."
                    className="min-h-[44px] max-h-[120px] resize-none rounded-lg text-sm"
                    disabled={isLoading}
                  />
                </div>
                {isLoading ? (
                  <Button
                    onClick={handleCancelRequest}
                    size="icon"
                    variant="outline"
                    className="h-11 w-11 rounded-lg shrink-0 text-destructive hover:text-destructive"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSend}
                    disabled={!input.trim()}
                    size="icon"
                    className="h-11 w-11 rounded-lg shrink-0"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1.5 text-center">
                Powered by Claude AI
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
